import { useEffect, useRef, useState } from "react";

import { useTypedSelector } from "store/reducers/use-typed-selector";
import { MuxPlaybackPolicy } from "types/mux-playback-policy";
import { Session, SessionPlaybackVideo, SessionTypesEnum, StaticVideo } from "types/working-model";
import { isMuxUrl } from "utils/mux-utils";

type Props = {
	session?: Session | null;
	currentDynamicVideo?: SessionPlaybackVideo;
	onDemandVideo?: StaticVideo;
};

const useValidateMuxPlaybackId = ({
	session,
	currentDynamicVideo,
	onDemandVideo
}: Props) => {
	const [muxPlaybackId, setMuxPlaybackId] = useState<string | null>(null);
	const [potentialMuxPlaybackId, setPotentialMuxPlaybackId] = useState<string | null>(null);

	const adminToken = useTypedSelector(state => state.AuthReducer.token);
	const profileToken = useTypedSelector(state => state.LiveEventReducer.blProfileUserToken);

	const streamingOptionPolicDrmEnabled = session?.streaming_options?.playback_policy && [MuxPlaybackPolicy.drm, MuxPlaybackPolicy.signed].includes(session?.streaming_options?.playback_policy);
	const drmEnabled = !!(streamingOptionPolicDrmEnabled);
	const sessionType = session?.session_type;
	const onDemandPlaybackUrl = onDemandVideo?.playback_url;
	const dynamicPlaybackUrl = currentDynamicVideo?.playback_url;
	const currentDynamicPlaybackPolicy = currentDynamicVideo?.policy;
	const checkingPolicy = useRef('');

	// detect if we have a potential playback id
	// we call this "potential" because there are scenarios where we set ondemand session videos to be recorded videos
	// and we don't know the specific state and if the id is actually valid.
	useEffect(() => {
		const url = dynamicPlaybackUrl;
		if (url && isMuxUrl(url) && url.includes('.m3u8')) {
			// extract the mux playback id from url path	
			const _url = new URL(url);
			const _potentialMuxPlaybackId = _url.pathname.replace(/^\/|\.m3u8/g, '');
			if (_potentialMuxPlaybackId) {
				setPotentialMuxPlaybackId(_potentialMuxPlaybackId);
			}
			// if we have both a mux stream and an hls_link, then let's pull the playback id from the hls link
		} else if (
			sessionType === SessionTypesEnum.onDemand
			&& onDemandPlaybackUrl?.includes('.m3u8')
		) {
			const _url = new URL(onDemandPlaybackUrl);
			const _potentialMuxPlaybackId = _url.pathname.replace(/^\/|\.m3u8/g, '');
			if (_potentialMuxPlaybackId) {
				setPotentialMuxPlaybackId(_potentialMuxPlaybackId);
			}
		}
	}, [dynamicPlaybackUrl, onDemandPlaybackUrl, sessionType]);

	// Use the potential playback id to get a policy, if we get one we know we have a mux playback id for sure.
	// Because on demand sessions can use recorded content, and recorded content can be streamed
	// while drm is enabled, there's no way to know if the recorded content is drm enabled, and as a result
	// so we fetch the policy details here if applicable
	useEffect(() => {
		if (!potentialMuxPlaybackId) return;
		if (potentialMuxPlaybackId === checkingPolicy.current) return;

		if (potentialMuxPlaybackId && potentialMuxPlaybackId !== muxPlaybackId) {
			setMuxPlaybackId(potentialMuxPlaybackId);
		}
	}, [adminToken, drmEnabled, muxPlaybackId, potentialMuxPlaybackId, profileToken, currentDynamicPlaybackPolicy]);

	return { muxPlaybackId, drmEnabled };
};

export default useValidateMuxPlaybackId;
